import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation, Trans } from 'react-i18next';
import Carousel from './Carousel';
import Youtube from './Youtube';
import { trackPageView, trackClick, trackVideoEvent } from '../utils/analytics';

const Simulator = (props) => {

  // Load videos from config based on language props
  const getVideosFromConfig = () => {
    const videoConfig = props.config?.videos?.[props.hreflang]?.main;
    if (!videoConfig) {
      // Fallback to hardcoded videos if config.js is not available
      return [
        { 
          src: 'https://d25totkniwo8m4.cloudfront.net/shopping.mp4', 
          presby: 'https://d25totkniwo8m4.cloudfront.net/shopping-presby.mp4', 
          mfcl: 'https://d25totkniwo8m4.cloudfront.net/shopping-mfcl.mp4',
          short_title: <Trans i18nKey="simulator.videos.shopping.short_title" />, 
        },
        { 
          src: 'https://d25totkniwo8m4.cloudfront.net/driving.mp4', 
          presby: 'https://d25totkniwo8m4.cloudfront.net/driving-presby.mp4', 
          mfcl: 'https://d25totkniwo8m4.cloudfront.net/driving-mfcl.mp4',
          short_title: <Trans i18nKey="simulator.videos.driving.short_title" />, 
        },
        { 
          src: 'https://d25totkniwo8m4.cloudfront.net/dining.mp4', 
          presby: 'https://d25totkniwo8m4.cloudfront.net/dining-presby.mp4', 
          mfcl: 'https://d25totkniwo8m4.cloudfront.net/dining-mfcl.mp4',
          short_title: <Trans i18nKey="simulator.videos.dining.short_title" />, 
        },
      ];
    }

    // Videos loaded from config.json
    return [
      {
        src: videoConfig.shopping?.video || 'https://d25totkniwo8m4.cloudfront.net/shopping-en-us.mp4',
        presby: 'https://d25totkniwo8m4.cloudfront.net/shopping-presby.mp4', // Keep existing presby variants
        mfcl: 'https://d25totkniwo8m4.cloudfront.net/shopping-mfcl.mp4',     // Keep existing mfcl variants
        short_title: <Trans i18nKey="simulator.videos.shopping.short_title" />,
      },
      {
        src: videoConfig.driving?.video || 'https://d25totkniwo8m4.cloudfront.net/driving-en-us.mp4',
        presby: 'https://d25totkniwo8m4.cloudfront.net/driving-presby.mp4',
        mfcl: 'https://d25totkniwo8m4.cloudfront.net/driving-mfcl.mp4',
        short_title: <Trans i18nKey="simulator.videos.driving.short_title" />,
      },
      {
        src: videoConfig.dining?.video || 'https://d25totkniwo8m4.cloudfront.net/dining-en-us.mp4',
        presby: 'https://d25totkniwo8m4.cloudfront.net/dining-presby.mp4',
        mfcl: 'https://d25totkniwo8m4.cloudfront.net/dining-mfcl.mp4',
        short_title: <Trans i18nKey="simulator.videos.dining.short_title" />,
      },
    ];
  };

  const videos = getVideosFromConfig();

  const { i18n, t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDialog, setShowDialog] = useState(true); // State to control dialog box visibility
  const [showCtaDialog, setShowCtaDialog] = useState(false); // New state for CTA dialog
  const [hasShownCtaDialog, setHasShownCtaDialog] = useState(false); // Track if CTA dialog has been shown
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16/9);
  const [showCarousel, setShowCarousel] = useState(false); // State to control carousel visibility
  const [showPresbyModal, setShowPresbyModal] = useState(false); // State for the top-left modal
  const [showMfclModal, setShowMfclModal] = useState(false); // State for the top-right modal
  const [showListModal, setShowListModal] = useState(false); // State for the list modal
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [darkenVideo, setDarkenVideo] = useState(true); // Control darkening overlay
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false); // Track scrolling

  // Video tracking state
  const [videoStartTracked, setVideoStartTracked] = useState(false);
  const [videoCompleteTracked, setVideoCompleteTracked] = useState(false);
  const [progressMilestones, setProgressMilestones] = useState(new Set());

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const dialogContentRef = useRef(null); // Ref for the dialog content
  const isDraggingRef = useRef(false);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const currentVideo = videos[currentVideoIndex];

  const whatIsPresby = props.config?.videos?.[props.hreflang]?.what_is_presby;

  // Calculate constants first
  const toolbarHeight = 50;
  const availableVideoHeight = viewportHeight - toolbarHeight;
  const availableWidth = window.innerWidth;
  const screenAspectRatio = availableWidth / availableVideoHeight;

  const navigate = useNavigate(); // Get the navigate function from React Router

  // Calculate video display dimensions - ensure it fits within available space
  let videoDisplayWidth, videoDisplayHeight;
  
  if (videoAspectRatio > screenAspectRatio) {
    // Video is wider than screen - fit to width
    videoDisplayWidth = availableWidth;
    videoDisplayHeight = availableWidth / videoAspectRatio;
  } else {
    // Video is taller than screen - fit to height
    videoDisplayHeight = availableVideoHeight;
    videoDisplayWidth = availableVideoHeight * videoAspectRatio;
  }

  // Ensure video never exceeds available space
  if (videoDisplayHeight > availableVideoHeight) {
    videoDisplayHeight = availableVideoHeight;
    videoDisplayWidth = availableVideoHeight * videoAspectRatio;
  }
  
  if (videoDisplayWidth > availableWidth) {
    videoDisplayWidth = availableWidth;
    videoDisplayHeight = availableWidth / videoAspectRatio;
  }

  // Detect the language before render
  useLayoutEffect(() => {
    i18n.changeLanguage(props.lan); // Set the language using the `lan` prop
  }, [i18n, props.lan]);

  const handlePlay = () => {
    // Track click event
    trackClick({
      componentName: 'simulator_dialog',
      type: 'button_click',
      linkText: 'Play',
      linkUrl: window.location.href,
      outbound: false
    });

    const videoElement = document.getElementById('main-video');
    if (videoElement) {
      videoElement.play();
      setIsPlaying(true); // Update playing state
      setShowDialog(false); // Hide the dialog box when the video starts playing
      setDarkenVideo(false); // Remove darkening when video starts playing
    }
    // Remove focus from the play button
    document.activeElement.blur();
  };

  useEffect(() => {
    // Media query to check for portrait orientation
    const mediaQuery = window.matchMedia("(orientation: portrait)");

    const handleOrientationChange = (e) => {
      if (e.matches) {
        // Before redirecting, save the current video index, playing state, current position
        // and also that the dialog has been dismissed
        sessionStorage.setItem('lastVideoIndex', currentVideoIndex.toString());
        sessionStorage.setItem('wasPlaying', isPlaying.toString());
        sessionStorage.setItem('dialogDismissed', (!showDialog).toString());
        
        // Save the current playback position if video is loaded
        if (videoRef.current && isFinite(videoRef.current.currentTime)) {
          sessionStorage.setItem('videoPosition', videoRef.current.currentTime.toString());
        }
        
        // If it's portrait mode, redirect to the proper language orientation page
        navigate(`/${props.hreflang}/orientation`);
      }
    };

    // 1. Initial check on load
    if (mediaQuery.matches) {
      navigate(`/${props.hreflang}/orientation`);
    }

    // 2. Add listener for orientation changes
    mediaQuery.addEventListener("change", handleOrientationChange);

    // 3. Cleanup function: important for preventing memory leaks
    return () => {
      mediaQuery.removeEventListener("change", handleOrientationChange);
    };
  }, [navigate, props.hreflang, currentVideoIndex, isPlaying, showDialog]); // Add isPlaying and showDialog dependencies

  // Track page view when component mounts or language changes
  useEffect(() => {
    trackPageView(props.hreflang);
  }, [props.hreflang]);

  // Retrieve saved video index, playing state, and position when component mounts
  useEffect(() => {
    const savedVideoIndex = sessionStorage.getItem('lastVideoIndex');
    const wasPlaying = sessionStorage.getItem('wasPlaying') === 'true';
    const savedPosition = sessionStorage.getItem('videoPosition');
    const dialogDismissed = sessionStorage.getItem('dialogDismissed') === 'true';
    
    if (savedVideoIndex !== null) {
      const index = parseInt(savedVideoIndex, 10);
      // Only update if it's a valid index
      if (!isNaN(index) && index >= 0 && index < videos.length) {
        setCurrentVideoIndex(index);
        
        // If the dialog was previously dismissed, keep it dismissed
        if (dialogDismissed) {
          setShowDialog(false);
          setDarkenVideo(false);
        }
        
        // If the video was playing before orientation change, 
        // don't show the dialog and set up to autoplay
        if (wasPlaying) {
          setShowDialog(false);
          setDarkenVideo(false);
          
          // Need a small timeout to ensure the video element is ready
          setTimeout(() => {
            if (videoRef.current) {
              // Restore the saved position if available
              if (savedPosition !== null) {
                const position = parseFloat(savedPosition);
                if (!isNaN(position) && isFinite(position)) {
                  videoRef.current.currentTime = position;
                }
              }
              
              videoRef.current.play().catch(err => {
                console.error('Error resuming video after orientation change:', err);
              });
              setIsPlaying(true);
            }
          }, 300);
        } else if (savedPosition !== null) {
          // If not playing, still restore position after video loads
          const position = parseFloat(savedPosition);
          if (!isNaN(position) && isFinite(position)) {
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.currentTime = position;
              }
            }, 300);
          }
        }
      }
    }
  }, [videos.length]);

  const handlePlayPause = () => {
    // Prevent play/pause if any dialog is showing
    if (showDialog || showCtaDialog) {
      return; // Exit early - don't allow play/pause when dialogs are showing
    }
    
    console.log('clicked play/pause');
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
        setIsPlaying(true);
      }
    }
  };

  // Add a click handler for the video
  const handleVideoClick = () => {
    // Prevent video click if any dialog is showing
    if (showDialog || showCtaDialog) {
      return; // Exit early - don't allow video to play when dialogs are showing
    }
    
    // Use the same logic as handlePlayPause to ensure consistency
    handlePlayPause();
  };

  // Handle video ended event to show CTA dialog or loop
  const handleVideoEnded = () => {
    // Track video completion
    handleVideoComplete();
    
    if (videoRef.current) {
      // If CTA dialog hasn't been shown yet, show it instead of looping
      if (!hasShownCtaDialog && !showDialog) {
        videoRef.current.pause();
        setIsPlaying(false);
        setShowCtaDialog(true);
        setDarkenVideo(true);
        setHasShownCtaDialog(true); // Mark that we've shown the dialog
      } else {
        // Reset to beginning and continue playing (original behavior)
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(err => {
          console.error('Error looping video:', err);
        });
      }
    }
  };

  // Handle closing the CTA dialog
  const handleCloseCtaDialog = () => {
    setShowCtaDialog(false);
    setDarkenVideo(false);
    
    // Resume playing the video when the dialog is closed
    if (videoRef.current) {
      videoRef.current.currentTime = 0; // Start from beginning
      videoRef.current.play().catch(err => {
        console.error('Error playing video after CTA dialog:', err);
      });
      setIsPlaying(true);
    }
  };

  // Reset duration when video changes
  useEffect(() => {
    // Reset duration when video source changes
    setDuration(0);
    setCurrentTime(0);
  }, [currentVideoIndex]);

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      const { videoWidth, videoHeight, duration } = videoRef.current;
      setVideoAspectRatio(videoWidth / videoHeight);
      
      // Set duration when metadata is loaded
      if (duration && isFinite(duration)) {
        setDuration(duration);
      }
    }
  };

  // Reset video tracking state when video changes
  useEffect(() => {
    setVideoStartTracked(false);
    setVideoCompleteTracked(false);
    setProgressMilestones(new Set());
  }, [currentVideoIndex]);

  // Video tracking functions
  const handleVideoStart = () => {
    if (!videoStartTracked && videoRef.current) {
      setVideoStartTracked(true);
      const video = videoRef.current;
      const currentVideoData = videos[currentVideoIndex];
      
      // Extract English-only video title (e.g., 'Dining' from 'simulator.videos.dining.short_title')
      const i18nKey = currentVideoData?.short_title?.props?.i18nKey;
      const videoTitle = i18nKey 
        ? i18nKey.split('.')[2]?.charAt(0).toUpperCase() + i18nKey.split('.')[2]?.slice(1)
        : `Video ${currentVideoIndex + 1}`;
      
      trackVideoEvent({
        eventType: 'video_start',
        currentTime: video.currentTime,
        duration: video.duration,
        videoTitle: videoTitle,
        videoUrl: currentVideoData?.src || '',
        visible: true
      });
    }
  };

  const handleVideoProgress = (currentTime, duration) => {
    if (duration > 0) {
      const percent = Math.round((currentTime / duration) * 100);
      const currentVideoData = videos[currentVideoIndex];
      
      // Track progress at 10%, 25%, 50%, 75% milestones
      const milestones = [10, 25, 50, 75];
      
      // Extract English-only video title (e.g., 'Dining' from 'simulator.videos.dining.short_title')
      const i18nKey = currentVideoData?.short_title?.props?.i18nKey;
      const videoTitle = i18nKey 
        ? i18nKey.split('.')[2]?.charAt(0).toUpperCase() + i18nKey.split('.')[2]?.slice(1)
        : `Video ${currentVideoIndex + 1}`;
      
      milestones.forEach(milestone => {
        if (percent >= milestone && !progressMilestones.has(milestone)) {
          setProgressMilestones(prev => new Set([...prev, milestone]));
          
          trackVideoEvent({
            eventType: 'video_progress',
            currentTime: currentTime,
            duration: duration,
            videoTitle: videoTitle,
            videoUrl: currentVideoData?.src || '',
            visible: true
          });
        }
      });
    }
  };

  const handleVideoComplete = () => {
    if (!videoCompleteTracked && videoRef.current) {
      setVideoCompleteTracked(true);
      const video = videoRef.current;
      const currentVideoData = videos[currentVideoIndex];
      
      // Extract English-only video title (e.g., 'Dining' from 'simulator.videos.dining.short_title')
      const i18nKey = currentVideoData?.short_title?.props?.i18nKey;
      const videoTitle = i18nKey 
        ? i18nKey.split('.')[2]?.charAt(0).toUpperCase() + i18nKey.split('.')[2]?.slice(1)
        : `Video ${currentVideoIndex + 1}`;
      
      trackVideoEvent({
        eventType: 'video_complete',
        currentTime: video.currentTime,
        duration: video.duration,
        videoTitle: videoTitle,
        videoUrl: currentVideoData?.src || '',
        visible: true
      });
    }
  };

  const handlePrevious = (event) => {
    // Prevent interaction when dialogs are visible
    if (showDialog || showCtaDialog) {
      return; // Exit early - don't allow navigation when dialogs are showing
    }
    
    // Track click event
    const prevVideoIndex = (currentVideoIndex - 1 + videos.length) % videos.length;
    // Get English-only text for analytics - extract video name from 'simulator.videos.dining.short_title'
    const prevVideoTitleKey = videos[prevVideoIndex]?.short_title?.props?.i18nKey;
    const prevVideoTitle = prevVideoTitleKey 
      ? prevVideoTitleKey.split('.')[2]?.charAt(0).toUpperCase() + prevVideoTitleKey.split('.')[2]?.slice(1)
      : 'Previous Video';
    trackClick({
      componentName: 'video controls',
      type: 'button_click',
      linkText: prevVideoTitle,
      linkUrl: window.location.href,
      outbound: false
    });
    
    setCurrentVideoIndex(prevVideoIndex);
    
    // Set isPlaying to true as we want the video to start playing automatically
    setIsPlaying(true);
    
    // Play the video after a short delay to allow the new source to load
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          setIsPlaying(false);
        });
      }
    }, 100);

    // Explicitly remove focus from the clicked element
    if (event && event.currentTarget) {
      event.currentTarget.blur();
    }
  };

  const handleNext = (event) => {
    // Prevent interaction when dialogs are visible
    if (showDialog || showCtaDialog) {
      return; // Exit early - don't allow navigation when dialogs are showing
    }
    
    // Track click event
    const nextVideoIndex = (currentVideoIndex + 1) % videos.length;
    // Get English-only text for analytics - extract video name from 'simulator.videos.dining.short_title'
    const nextVideoTitleKey = videos[nextVideoIndex]?.short_title?.props?.i18nKey;
    const nextVideoTitle = nextVideoTitleKey 
      ? nextVideoTitleKey.split('.')[2]?.charAt(0).toUpperCase() + nextVideoTitleKey.split('.')[2]?.slice(1)
      : 'Next Video';
    trackClick({
      componentName: 'video controls',
      type: 'button_click', 
      linkText: nextVideoTitle,
      linkUrl: window.location.href,
      outbound: false
    });
    
    setCurrentVideoIndex(nextVideoIndex);
    
    // Set isPlaying to true as we want the video to start playing automatically
    setIsPlaying(true);
    
    // Play the video after a short delay to allow the new source to load
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          setIsPlaying(false);
        });
      }
    }, 100);

    // Explicitly remove focus from the clicked element
    if (event && event.currentTarget) {
      event.currentTarget.blur();
    }
  };

  // Add effect to handle video source changes and autoplay
  useEffect(() => {
    // When video source changes, we need to wait for it to load before playing
    if (videoRef.current) {
      const handleCanPlay = () => {
        if (isPlaying) {
          videoRef.current.play().catch(err => {
            console.error('Error playing video after source change:', err);
            setIsPlaying(false);
          });
        }
      };
      
      // Add event listener for canplay event
      videoRef.current.addEventListener('canplay', handleCanPlay);
      
      // Clean up
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
  }, [currentVideoIndex, isPlaying]);

  const handleAskForMfcls = () => {
    // Track click event
    trackClick({
      componentName: 'video controls',
      type: 'button_click',
      linkText: 'ask for ACUVUE MFCLs',
      linkUrl: props.config["simulator"][props.hreflang]["cta_bottom"],
      outbound: true
    });

    console.log(`ask for mfcl link: ${props.config["simulator"][props.hreflang]["cta_bottom"]}`);
    
    // Pause the video if it's currently playing
    if (isPlaying && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    
    // Use the same URL as the CTA button from the configuration
    window.open(props.config["simulator"][props.hreflang]["cta_bottom"], '_blank');
  };

  const handleListClick = () => {
    // Prevent interaction when dialogs are visible
    if (showDialog || showCtaDialog) {
      return; // Exit early - don't allow list functionality when dialogs are showing
    }
    
    // If video is playing, pause it when opening the carousel
    if (isPlaying) {
      const videoElement = document.getElementById('main-video');
      if (videoElement) {
        videoElement.pause(); 
        setIsPlaying(false); // Update playing state to reflect paused state
      }
    }
    
    // Show the carousel and darken the video
    setShowCarousel(true);
    setDarkenVideo(true);
  };

  const handleCloseCarousel = () => {
    setShowCarousel(false); // Hide the carousel
    setDarkenVideo(showDialog || showCtaDialog); // Only keep darkened if any dialog is showing
  };

  const handleVideoSelect = (videoSrc, autoPlay = false) => {
    // Find the index of the selected video
    const newIndex = videos.findIndex((video) => video.src === videoSrc);
    
    // Set the current video index
    setCurrentVideoIndex(newIndex); 
    
    // Close the carousel
    setShowCarousel(false);
    
    if (autoPlay) {
      // Auto-play the video without showing dialog
      setIsPlaying(true);
      setShowDialog(false);
      
      // Play the video after a short delay to allow the new source to load
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.error('Error playing video:', err);
            setIsPlaying(false);
          });
        }
      }, 100);
    } else {
      // Make sure the video is paused and show dialog
      setIsPlaying(false);
      setShowDialog(true);
      
      // Ensure the video is actually paused in case autoplay tries to kick in
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  };

  // Update the progress bar click handler to be usable again
  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current) return;

    const progressBar = progressBarRef.current;
    const bounds = progressBar.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const width = bounds.width;
    const percent = Math.max(0, Math.min(1, x / width)); // Ensure percent is between 0 and 1

    const video = document.getElementById('main-video');
    if (video && isFinite(video.duration)) {
      const newTime = percent * video.duration;
      video.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };
  
  // Separate function for dragging
  const handleProgressBarDrag = useCallback((e) => {
    if (!isDraggingRef.current) return; // Only process if actively dragging - use ref
    
    // Use the appropriate client position based on device type
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    
    if (!progressBarRef.current) return;
    
    const progressBar = progressBarRef.current;
    const bounds = progressBar.getBoundingClientRect();
    const x = clientX - bounds.left;
    const width = bounds.width;
    const percent = Math.max(0, Math.min(1, x / width)); // Ensure percent is between 0 and 1

    const video = document.getElementById('main-video');
    if (video && isFinite(video.duration)) {
      const newTime = percent * video.duration;
      video.currentTime = newTime;
      requestAnimationFrame(() => {
        setCurrentTime(newTime);
      });
    }
  }, []);

  // Simplified touch handling for the progress bar and handle
  const handleProgressBarTouch = useCallback((e) => {
    e.preventDefault(); // Prevent scrolling
    e.stopPropagation();
    
    if (!progressBarRef.current || !e.touches[0]) return;
    
    const progressBar = progressBarRef.current;
    const bounds = progressBar.getBoundingClientRect();
    const x = e.touches[0].clientX - bounds.left;
    const width = bounds.width;
    const percent = Math.max(0, Math.min(1, x / width));

    const video = document.getElementById('main-video');
    if (video && isFinite(video.duration)) {
      const newTime = percent * video.duration;
      video.currentTime = newTime;
      requestAnimationFrame(() => {
        setCurrentTime(newTime);
      });
    }
  }, []);

  // Mouse down initiates drag
  const handleProgressBarMouseDown = (e) => {
    // Don't call preventDefault for touch events in the handler itself
    if (!e.touches) {
      e.preventDefault(); // Prevent default behavior for mouse events only
    }
    
    isDraggingRef.current = true;
    
    // Get the client position based on device type
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    
    if (!progressBarRef.current) return;
    
    const progressBar = progressBarRef.current;
    const bounds = progressBar.getBoundingClientRect();
    const x = clientX - bounds.left;
    const width = bounds.width;
    const percent = Math.max(0, Math.min(1, x / width));

    const video = document.getElementById('main-video');
    if (video && isFinite(video.duration)) {
      const newTime = percent * video.duration;
      video.currentTime = newTime;
      requestAnimationFrame(() => {
        setCurrentTime(newTime);
      });
    }
    
    // Add event listeners for dragging
    document.addEventListener('mousemove', handleProgressBarDrag);
    document.addEventListener('mouseup', handleProgressBarMouseUp);
  };

  const handleProgressBarMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    
    // Remove event listeners
    document.removeEventListener('mousemove', handleProgressBarDrag);
    document.removeEventListener('mouseup', handleProgressBarMouseUp);
  }, [handleProgressBarDrag]);
  
  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      // Only need to clean up document event listeners that might persist
      document.removeEventListener('mousemove', handleProgressBarDrag);
      document.removeEventListener('mouseup', handleProgressBarMouseUp);
    };
  }, [handleProgressBarDrag, handleProgressBarMouseUp]);

  const handleTimeUpdate = () => {
    const video = document.getElementById('main-video');
    if (video) {
      setCurrentTime(video.currentTime); // Update the current time
      if (!duration && video.duration) {
        setDuration(video.duration); // Ensure duration is set if not already
      }
      
      // Track video progress milestones
      if (video.duration > 0) {
        handleVideoProgress(video.currentTime, video.duration);
      }
    }
  };

  const handleShowMfclModal = () => {
    // Track click event
    trackClick({
      componentName: 'video overlay',
      type: 'link_click',
      linkText: 'Seamless vision with ACUVUE MFCLs',
      linkUrl: window.location.href,
      outbound: false
    });
    
    // Pause the video if it's playing
    if (isPlaying && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false); // Update playing state to reflect paused state
    }
    setShowMfclModal(true);
    setDarkenVideo(true); // Darken the video
  };

  const handleShowPresbyModal = () => {
    // Track click event
    trackClick({
      componentName: 'video overlay',
      type: 'button_click',
      linkText: 'Vision with Presbyopia',
      linkUrl: window.location.href,
      outbound: false
    });
    
    const videoElement = document.getElementById('main-video');
    if (videoElement) {
      videoElement.pause();
      setIsPlaying(false);
    }

    setShowPresbyModal(true);
    setDarkenVideo(true);
  };

  const getPreviousVideo = () => videos[(currentVideoIndex - 1 + videos.length) % videos.length];
  const getNextVideo = () => videos[(currentVideoIndex + 1) % videos.length];

  const preventFocus = (e) => {
    e.currentTarget.blur();
  };

  // Simple scroll behavior with no complicated scrollbar management
  const handleDialogScroll = () => {
    if (!dialogContentRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = dialogContentRef.current;
    // Consider "bottom" as within 10px of the actual bottom to account for rounding errors
    const isBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    if (isBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Simplified sessionStorage check without the test condition
  useEffect(() => {
    // Check if the user has been to the welcome page in this session
    const hasVisitedWelcome = sessionStorage.getItem('hasVisitedWelcome');
    
    // If they haven't visited the welcome page, redirect
    if (!hasVisitedWelcome) {
      console.log('Redirecting to welcome page - session check failed');
      navigate(`/${props.hreflang}`);
    }
  }, [navigate, props.hreflang]);

  // Handle viewport height changes (browser bar showing/hiding)
  useEffect(() => {
    const handleResize = () => {
      // Immediately update with current height
      setViewportHeight(window.innerHeight);
      
      // Schedule additional updates to catch delayed UI changes
      setTimeout(() => setViewportHeight(window.innerHeight), 100);
      setTimeout(() => setViewportHeight(window.innerHeight), 300);
    };

    const handleVisibilityChange = () => {
      // Force multiple resize checks when visibility changes (browser bar toggle)
      handleResize();
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen for page show/hide events which can occur during navigation
    window.addEventListener('pageshow', handleResize);
    window.addEventListener('focus', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handleResize);
      window.removeEventListener('focus', handleResize);
    };
  }, []);

  // Forcibly recalculate viewport dimensions after navigation
  useEffect(() => {
    // Force multiple recalculations of viewport height after component mounts
    // This helps address issues when browser UI appears/disappears during transitions
    const recalculateViewport = () => {
      setViewportHeight(window.innerHeight);
    };
    
    // Immediately recalculate
    recalculateViewport();
    
    // Then recalculate after short delays to catch any browser UI changes
    const timers = [
      setTimeout(recalculateViewport, 100),
      setTimeout(recalculateViewport, 300),
      setTimeout(recalculateViewport, 500),
      setTimeout(recalculateViewport, 1000)
    ];
    
    return () => {
      // Clean up all timers
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []); // Only run on mount

  // Load video and extract first frame
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [currentVideoIndex]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.volume = volume;
    }
  }, [isMuted, volume, currentVideoIndex]);

  const handleToggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const handleVolumeChange = (e) => {
    const nextVolume = Number(e.target.value);
    setVolume(nextVolume);
    if (nextVolume > 0 && isMuted) {
      setIsMuted(false);
    }
    if (nextVolume === 0 && !isMuted) {
      setIsMuted(true);
    }
  };

  return (
    <>
      
      <div 
        ref={containerRef}
        className={`position-relative d-flex flex-column lang-${props.lan}`}
        style={{ height: `${availableVideoHeight}px` }}
      >
        {/* Video Container */}
        <div 
          className="flex-grow-1 d-flex align-items-center justify-content-center"
        >
          <div className="position-relative">
            <video
              ref={videoRef}
              className="d-block mx-auto"
              id="main-video"
              style={{ 
                width: `${videoDisplayWidth}px`,
                height: `${videoDisplayHeight}px`,
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              controls={false}
              muted={isMuted}
              playsInline
              webkit-playsinline="true"
              onPlay={() => {
                setIsPlaying(true);
                handleVideoStart();
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={handleVideoEnded}
              onLoadedMetadata={handleVideoLoadedMetadata}
              preload="auto"
              src={currentVideo.src}
              onTimeUpdate={handleTimeUpdate}
              onClick={handleVideoClick}
              onError={(e) => {
                console.error('Video loading failed:', {
                  src: currentVideo.src,
                  error: e.target.error,
                  networkState: e.target.networkState,
                  readyState: e.target.readyState,
                  currentSrc: e.target.currentSrc
                });
              }}
            >
            </video>

            {/* Darken overlay when any UI element is shown over the video */}
            {darkenVideo && <div className="video-darken-overlay"></div>}

            {/* Clickable Title Areas */}
            <div className="clickable-overlay-container">
              <div 
                className={`clickable-title-area left ${(showDialog || showCtaDialog) ? 'clickable-area-disabled' : ''}`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the video click handler
                  // Only handle the click if dialogs are not showing
                  if (!showDialog && !showCtaDialog) {
                    handleShowPresbyModal();
                  }
                }}
                title={t('simulator.information.presbyopia_info')}
              ></div>
              <div 
                className={`clickable-title-area right ${(showDialog || showCtaDialog) ? 'clickable-area-disabled' : ''}`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the video click handler
                  // Only handle the click if dialogs are not showing
                  if (!showDialog && !showCtaDialog) {
                    handleShowMfclModal();
                  }
                }}
                title={t('simulator.information.mfcl_info')}
              ></div>
            </div>

          </div>

          {/* Initial Dialog Box - only shown on first component mount */}
          {showDialog && (
            <div className="dialog-box-initial fade-in">
              <div 
                id="dialog-box-initial-content"
                ref={dialogContentRef} 
                onScroll={handleDialogScroll}
                className={hasScrolledToBottom ? 'scrolled-to-bottom' : ''}
              >
                <h3 className="dialog-title text-center"><Trans i18nKey="simulator.dialog.common.title" components={{ sup: <sup /> }} /></h3>
                <p className="dialog-text mb-4"><Trans i18nKey="simulator.dialog.common.content1" /></p>
                <p className="dialog-text"><Trans i18nKey="simulator.dialog.common.content2" components={{ sup: <sup /> }} /></p>
                <p className="dialog-text"><Trans i18nKey="simulator.dialog.common.content3" /></p>
                <p className="dialog-text mb-4"><Trans i18nKey="simulator.dialog.initial.content1" /></p>
                <p className="dialog-text">
                  <Trans i18nKey="simulator.dialog.initial.content2" components={{ sup: <sup /> }} />
                  {props.hreflang === 'zh-hk' || props.hreflang === 'fr-ca' || props.hreflang === 'ja-jp' ? (
                    <a className="instruction-guide" href={props.config["simulator"][props.hreflang]["patient_guide"]} target="_blank"><Trans i18nKey="simulator.dialog.initial.guide_text" /></a>
                  ) : (
                    <>
                      <a className="instruction-guide" href={props.config["simulator"][props.hreflang]["patient_guide"]} target="_blank"><Trans i18nKey="simulator.dialog.initial.guide_text_us" /></a> or <a className="instruction-guide" href="https://www.acuvue.com/en-ca/patient-instruction-guides/" target="_blank"><Trans i18nKey="simulator.dialog.initial.guide_text_canada" /></a>
                    </>
                  )}.
                </p>
                <p className="dialog-text"><Trans i18nKey="mlr.rev_num" /></p>
              </div>
              {/* Scroll indicator - only show when not scrolled to bottom */}
              {!hasScrolledToBottom && (
                <div className="scroll-indicator">
                  <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                  <div className="scroll-message-text">
                    <Trans i18nKey="actions.scroll_to_continue" />
                  </div>
                </div>
              )}
              <hr className="dialog-divider" />
              <div className="text-end">
                {hasScrolledToBottom && (
                  <button 
                    className="acu-btn" 
                    onClick={handlePlay}
                    title="Play video"
                  >
                    <Trans i18nKey="actions.play" /> <FontAwesomeIcon icon={['fas', 'arrow-right']} className="ms-2 mt-1" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* CTA Dialog Box - shown when video ends (only once) */}
          {showCtaDialog && (
            <div className="dialog-box-cta fade-in">
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleCloseCtaDialog}
              >
                <FontAwesomeIcon icon={['fas', 'xmark']} />
              </button>
              <div>
                <p className="dialog-text text-center"><Trans i18nKey="simulator.dialog.cta.content" components={{ sup: <sup /> }} /></p>
              </div>
              <div className="text-center">
                <a 
                  href={props.config["simulator"][props.hreflang]["cta"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="acu-btn unfocusable-link"
                  onClick={() => {
                    trackClick({
                      componentName: 'cta dialog',
                      type: 'link_click',
                      linkText: 'Learn more',
                      linkUrl: props.config["simulator"][props.hreflang]["cta"],
                      outbound: true
                    });
                  }}
                >
                  <Trans i18nKey="actions.cta" /> <FontAwesomeIcon icon={['fas', 'arrow-up-right-from-square']} className="ms-2 mt-1" />
                </a>
              </div>
            </div>
          )}

        </div>

        {/* Toolbar - Width matches video display width */}
        <div 
          className="bg-dark border-top border-secondary mx-auto"
          style={{ 
            height: `${toolbarHeight}px`,
            minHeight: `${toolbarHeight}px`,
            width: `${videoDisplayWidth}px`,
            maxWidth: '100%'
          }}
        >
          <div className="container-fluid h-100 toolbar">
            <div className="row h-100">
              <div className="col px-0">
                <div className="progress-container" 
                     onClick={handleProgressBarClick}
                     onMouseDown={handleProgressBarMouseDown}
                     onTouchStart={handleProgressBarTouch}
                     onTouchMove={handleProgressBarTouch}
                     ref={progressBarRef}
                >
                  <div
                    className="progress-bar"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  >
                    <div 
                      className="progress-handle"
                      style={{ left: `calc(${(currentTime / duration) * 100}% - 5px)` }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleProgressBarMouseDown(e);
                      }}
                    ></div>
                  </div>
                </div>
                <div className="row align-items-center justify-content-between text-white flex-nowrap">
                  
                  <div className="col d-flex align-items-center px-0 toolbar-left">
                    {/* Play/Pause Button */}
                    <button 
                      className="btn btn-link text-white"
                      onClick={handlePlayPause}
                      disabled={showDialog || showCtaDialog}
                      style={{ 
                        opacity: (showDialog || showCtaDialog) ? 0.5 : 1, 
                        cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer' 
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', `${isPlaying ? 'fa-pause' : 'fa-play'}`]} />    
                    </button>

                    {/* List Button */}
                    <button 
                      className="btn btn-link text-white"
                      onClick={handleListClick}
                      disabled={showDialog || showCtaDialog}
                      style={{ 
                        opacity: (showDialog || showCtaDialog) ? 0.5 : 1, 
                        cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer' 
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'fa-list']} />    
                    </button>

                    {/* Audio controls - hidden entirely for ja-jp (video stays muted, no UI) */}
                    {props.hreflang !== 'ja-jp' && (
                      <>
                        <button
                          className="btn btn-link text-white"
                          onClick={handleToggleMute}
                          disabled={showDialog || showCtaDialog}
                          style={{
                            opacity: (showDialog || showCtaDialog) ? 0.5 : 1,
                            cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer'
                          }}
                          title={isMuted ? 'Unmute' : 'Mute'}
                        >
                          <FontAwesomeIcon icon={['fas', isMuted || volume === 0 ? 'volume-xmark' : 'volume-high']} />
                        </button>

                        {/* Volume slider - only shown in landscape to avoid toolbar overflow on mobile portrait */}
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          disabled={showDialog || showCtaDialog}
                          aria-label="Video volume"
                          className="show-landscape"
                          style={{
                            width: '100px',
                            marginLeft: '4px',
                            marginTop: '10px',
                            opacity: (showDialog || showCtaDialog) ? 0.5 : 1,
                            cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer'
                          }}
                        />
                      </>
                    )}

                    {/* Ask for ACUVUE MFCLs Link */}
                    <div className="ask-mfcl-wrapper">
                      <button 
                        className="btn btn-link btn-help text-white p-2 text-decoration-none"
                        onClick={handleAskForMfcls}
                        disabled={showDialog || showCtaDialog}
                        style={{ 
                          opacity: (showDialog || showCtaDialog) ? 0.5 : 1, 
                          cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer' 
                        }}
                      >
                        <span className="ask-mfcl-label">
                          <Trans 
                            i18nKey="simulator.controls.ask_mfcl" 
                            components={{ 
                              sup: <sup style={{ marginRight: '2px' }} /> 
                            }} 
                          />
                        </span>
                        <FontAwesomeIcon icon={['fas', 'arrow-up-right-from-square']} className="ms-2 mb-1 flex-shrink-0" />
                      </button>
                    </div>
                  </div>

                  <div className="col-auto d-flex flex-shrink-0">
                    {/* Previous Button */}
                    <button 
                      className="btn btn-link text-white"
                      onClick={handlePrevious}
                      disabled={showDialog || showCtaDialog}
                      style={{ 
                        opacity: (showDialog || showCtaDialog) ? 0.5 : 1, 
                        cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer' 
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'arrow-left']} className="me-1" />
                      <span className="show-landscape nav-links">
                        {getPreviousVideo().short_title} 
                      </span>   
                    </button>

                    {/* Next Button */}
                    <button 
                      className="btn btn-link text-white"
                      onClick={handleNext}
                      onMouseDown={preventFocus}
                      disabled={showDialog || showCtaDialog}
                      style={{ 
                        opacity: (showDialog || showCtaDialog) ? 0.5 : 1, 
                        cursor: (showDialog || showCtaDialog) ? 'default' : 'pointer' 
                      }}
                    >
                      <span className="show-landscape nav-links">
                        {getNextVideo().short_title}
                      </span>
                      <FontAwesomeIcon icon={['fas', 'arrow-right']} className="ms-1" />
                    </button>
                    
                  </div>

                </div>

              </div>             
            </div>

          </div>
        </div>

        {/* Carousel */}
        {showCarousel && (
          <Carousel
            onClose={handleCloseCarousel}
            onVideoSelect={handleVideoSelect}
            lan={props.lan} // Only send the `lan` prop
            hreflang={props.hreflang} // Only the `hreflang` prop
            config={props.config}
            currentVideoIndex={currentVideoIndex} // Pass the current video index
          />
        )}

        {/* List Modal */}
        {showListModal && (
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">List Modal</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowListModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>List clicked</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bootstrap Modal for Vision with Presbyopia */}
        {showPresbyModal && (
          props.hreflang === 'ja-jp' ? (
            <div className="modal show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog" role="document">
                <div className="modal-content modal-what-is">
                  <div className="modal-header">
                    <h5 className="modal-title">老眼とは？</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => {
                        setShowPresbyModal(false);
                        setDarkenVideo(showDialog || showCtaDialog);
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'xmark']} />
                    </button>
                  </div>
                  <div className="modal-body">
                    <p>
                      老眼とは、加齢により目のピントを合わせる機能が低下し、遠くが見える状態のままでは近くが見えない状態です。小さい文字が読みづらい、薄暗い中で文字が見えづらい、遠く・近くのものを見るとき、素早く焦点を合わせづらいといった症状があります。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Youtube
              video={`${props.config["videos"][props.hreflang]["what_is_presby"]["target"]}${props.config["videos"][props.hreflang]["what_is_presby"]["target"].includes('?') ? '&' : '?'}autoplay=1`}
              onClose={() => {
                setShowPresbyModal(false);
                setDarkenVideo(showDialog || showCtaDialog);
              }}
            />
          )
        )}

        {/* Bootstrap Modal for Vision with MFCLs */}
        {showMfclModal && (
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content modal-what-is">
                <div className="modal-header">
                  <h5 className="modal-title"><Trans i18nKey="simulator.modal.title" /></h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => {
                      setShowMfclModal(false);
                      setDarkenVideo(showDialog); // Only keep darkened if dialog is showing
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'xmark']} />
                  </button>
                </div>
                <div className="modal-body">
                  <p><Trans i18nKey="simulator.modal.content" components={{ sup: <sup /> }} /></p>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>

    </>
  );
};

export default Simulator;